/**
 * Generated by scripts/generate-drizzle-schema.mjs. Do not edit by hand.
 * This product's inputs: scripts/drizzle-schema.config.mjs.
 * Regenerate: bash scripts/client-schema-fresh.sh (data-model-change skill).
 */

import { DrizzleAppSchema, type PowerSyncSQLiteDatabase } from "@powersync/drizzle-driver";
import { index, integer, sqliteTable, text } from "drizzle-orm/sqlite-core";
import type { InferInsertModel, InferSelectModel } from "drizzle-orm";

// User assets (car, motorcycle, house, appliance…).
export const asset = sqliteTable(
  "asset",
  {
    id: text("id").primaryKey(),
    user_id: text("user_id"),
    name: text("name"),
    kind: text("kind"),
    icon: text("icon"),
    photo_url: text("photo_url"),
    license_plate: text("license_plate"),
    make: text("make"),
    model: text("model"),
    year: integer("year"),
    purchase_date: text("purchase_date"),
    usage_unit: text("usage_unit"),
    notes: text("notes"),
    archived: integer("archived"),
    sort_order: integer("sort_order"),
    deleted_at: text("deleted_at"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
    organization_id: text("organization_id"),
  },
  (table) => [
    /** WHERE user_id = ? ORDER BY sort_order in useAssetsQuery */
    index("asset_by_user").on(table.user_id, table.sort_order),
  ],
);

export const attachment = sqliteTable(
  "attachment",
  {
    id: text("id").primaryKey(),
    filename: text("filename"),
    content_type: text("content_type"),
    size: integer("size"),
    url: text("url"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
    list_id: text("list_id"),
    user_id: text("user_id"),
    is_shared: integer("is_shared"),
    hash: text("hash"),
    asset_id: text("asset_id"),
    service_record_id: text("service_record_id"),
    warranty_id: text("warranty_id"),
    organization_id: text("organization_id"),
  },
  (table) => [
    /** WHERE asset_id = ? ORDER BY created_at in asset documents */
    index("attachment_by_asset").on(table.asset_id, table.created_at),
    /** invoice(s) of a service */
    index("attachment_by_service").on(table.service_record_id),
    /** invoice(s) of a warranty */
    index("attachment_by_warranty").on(table.warranty_id),
    /** WHERE user_id = ? in getHasLocalDataQuery */
    index("attachment_by_user").on(table.user_id),
  ],
);

export const font_family = sqliteTable("font_family", {
  id: text("id").primaryKey(),
  family: text("family"),
  name: text("name"),
  category: text("category"),
  is_active: integer("is_active"),
  sort_order: integer("sort_order"),
  created_at: text("created_at"),
  updated_at: text("updated_at"),
});

export const font_variant = sqliteTable("font_variant", {
  id: text("id").primaryKey(),
  font_family_id: text("font_family_id"),
  weight: text("weight"),
  url: text("url"),
  created_at: text("created_at"),
  attachment_id: text("attachment_id"),
});

// Small dataset per user — indexes not needed for now.
export const member = sqliteTable("member", {
  id: text("id").primaryKey(),
  organization_id: text("organization_id"),
  user_id: text("user_id"),
  role: text("role"),
  created_at: text("created_at"),
});

// Small dataset — indexes not needed for now.
export const organization = sqliteTable("organization", {
  id: text("id").primaryKey(),
  name: text("name"),
  slug: text("slug"),
  logo: text("logo"),
  metadata: text("metadata"),
  created_at: text("created_at"),
});

// DUAL reminder rules (date and/or usage).
export const reminder_rule = sqliteTable(
  "reminder_rule",
  {
    id: text("id").primaryKey(),
    user_id: text("user_id"),
    asset_id: text("asset_id"),
    title: text("title"),
    category: text("category"),
    interval_months: integer("interval_months"),
    interval_usage: integer("interval_usage"),
    last_done_at: text("last_done_at"),
    last_done_usage: integer("last_done_usage"),
    lead_days: integer("lead_days"),
    active: integer("active"),
    note: text("note"),
    deleted_at: text("deleted_at"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
    organization_id: text("organization_id"),
  },
  (table) => [
    /** WHERE asset_id = ? AND active = 1 in rules by asset */
    index("reminder_rule_by_asset").on(table.asset_id, table.active),
    /** WHERE user_id = ? AND active = 1 in upcoming due dates */
    index("reminder_rule_by_user").on(table.user_id, table.active),
  ],
);

// ⚡ Largest domain table: history of services/maintenance.
export const service_record = sqliteTable(
  "service_record",
  {
    id: text("id").primaryKey(),
    user_id: text("user_id"),
    asset_id: text("asset_id"),
    category: text("category"),
    title: text("title"),
    performed_at: text("performed_at"),
    cost_cents: integer("cost_cents"),
    currency: text("currency"),
    usage_value: integer("usage_value"),
    vendor: text("vendor"),
    note: text("note"),
    reminder_rule_id: text("reminder_rule_id"),
    deleted_at: text("deleted_at"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
    organization_id: text("organization_id"),
  },
  (table) => [
    /** WHERE asset_id = ? ORDER BY performed_at DESC in useServiceRecordsQuery */
    index("service_record_by_asset").on(table.asset_id, table.performed_at),
    /** WHERE user_id = ? in annual costs and getHasLocalDataQuery */
    index("service_record_by_user").on(table.user_id, table.performed_at),
    /** latest service matching a rule (reminder reset) */
    index("service_record_by_rule").on(table.reminder_rule_id, table.performed_at),
  ],
);

// Active user sessions — small dataset, no indexes.
export const session = sqliteTable("session", {
  id: text("id").primaryKey(),
});

// Small global dataset (~20 themes) — indexes not needed.
export const themes = sqliteTable("themes", {
  id: text("id").primaryKey(),
  name: text("name"),
  background: text("background"),
  surface: text("surface"),
  accent: text("accent"),
  glow_colors: text("glow_colors"),
  created_at: text("created_at"),
  updated_at: text("updated_at"),
  is_dark: integer("is_dark"),
  foreground: text("foreground"),
  field_background: text("field_background"),
  danger: text("danger"),
  success: text("success"),
  warning: text("warning"),
  muted: text("muted"),
  pattern_type: text("pattern_type"),
  pattern_opacity: integer("pattern_opacity"),
  pair_id: text("pair_id"),
  font_normal: text("font_normal"),
  font_medium: text("font_medium"),
  font_semibold: text("font_semibold"),
  font_bold: text("font_bold"),
  radius: text("radius"),
  surface_foreground: text("surface_foreground"),
  surface_secondary: text("surface_secondary"),
  surface_secondary_foreground: text("surface_secondary_foreground"),
  surface_tertiary: text("surface_tertiary"),
  surface_tertiary_foreground: text("surface_tertiary_foreground"),
  overlay: text("overlay"),
  overlay_foreground: text("overlay_foreground"),
  default: text("default"),
  default_foreground: text("default_foreground"),
  accent_foreground: text("accent_foreground"),
  field_foreground: text("field_foreground"),
  field_placeholder: text("field_placeholder"),
  field_border: text("field_border"),
  danger_foreground: text("danger_foreground"),
  success_foreground: text("success_foreground"),
  warning_foreground: text("warning_foreground"),
  segment: text("segment"),
  segment_foreground: text("segment_foreground"),
  border: text("border"),
  focus: text("focus"),
  link: text("link"),
  surface_shadow: text("surface_shadow"),
  overlay_shadow: text("overlay_shadow"),
  field_shadow: text("field_shadow"),
  muted_foreground: text("muted_foreground"),
  divider: text("divider"),
  glass_background: text("glass_background"),
  glass_border: text("glass_border"),
  glass_highlight: text("glass_highlight"),
  is_active: integer("is_active"),
  sort_order: integer("sort_order"),
});

// Readings history (km/hours) — foundation for due projection.
export const usage_reading = sqliteTable(
  "usage_reading",
  {
    id: text("id").primaryKey(),
    user_id: text("user_id"),
    asset_id: text("asset_id"),
    value: integer("value"),
    recorded_at: text("recorded_at"),
    note: text("note"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
    organization_id: text("organization_id"),
  },
  (table) => [
    /** WHERE asset_id = ? ORDER BY recorded_at DESC in useUsageReadingsQuery and due projection */
    index("usage_reading_by_asset").on(table.asset_id, table.recorded_at),
    /** WHERE user_id = ? in getHasLocalDataQuery */
    index("usage_reading_by_user").on(table.user_id),
  ],
);

// Only 1 row per user — indexes not needed.
export const user = sqliteTable("user", {
  id: text("id").primaryKey(),
  name: text("name"),
  email: text("email"),
  email_verified: integer("email_verified"),
  image: text("image"),
  created_at: text("created_at"),
  updated_at: text("updated_at"),
  locale: text("locale"),
  global_theme_id: text("global_theme_id"),
  is_blur: integer("is_blur"),
  font_family: text("font_family"),
  follow_device_theme: integer("follow_device_theme"),
  role: text("role"),
});

// Warranties with materialized expiration.
export const warranty = sqliteTable(
  "warranty",
  {
    id: text("id").primaryKey(),
    user_id: text("user_id"),
    asset_id: text("asset_id"),
    title: text("title"),
    purchase_date: text("purchase_date"),
    duration_months: integer("duration_months"),
    expires_at: text("expires_at"),
    vendor: text("vendor"),
    note: text("note"),
    deleted_at: text("deleted_at"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
    organization_id: text("organization_id"),
  },
  (table) => [
    /** WHERE asset_id = ? ORDER BY expires_at in warranties by asset */
    index("warranty_by_asset").on(table.asset_id, table.expires_at),
    /** WHERE user_id = ? ORDER BY expires_at in upcoming due dates */
    index("warranty_by_user").on(table.user_id, table.expires_at),
  ],
);

export const drizzleSchema = {
  asset,
  attachment,
  font_family,
  font_variant,
  member,
  organization,
  reminder_rule,
  service_record,
  session,
  themes,
  usage_reading,
  user,
  warranty,
};

export const AppSchema = new DrizzleAppSchema(drizzleSchema);

export type Database = (typeof AppSchema)["types"];

/**
 * Type helper for the Drizzle database instance wrapped with PowerSync.
 */
export type PowerSyncDrizzleDB = PowerSyncSQLiteDatabase<typeof drizzleSchema>;

export const USER_DATA_TABLES = [
  "asset",
  "attachment",
  "reminder_rule",
  "service_record",
  "usage_reading",
  "warranty",
] as const;

export type Asset = InferSelectModel<typeof asset>;
export type AssetInsert = InferInsertModel<typeof asset>;
export type AssetRecord = Asset;
export type Attachment = InferSelectModel<typeof attachment>;
export type AttachmentInsert = InferInsertModel<typeof attachment>;
export type AttachmentRecord = Attachment;
export type FontFamily = InferSelectModel<typeof font_family>;
export type FontFamilyInsert = InferInsertModel<typeof font_family>;
export type FontFamilyRecord = FontFamily;
export type FontVariant = InferSelectModel<typeof font_variant>;
export type FontVariantInsert = InferInsertModel<typeof font_variant>;
export type FontVariantRecord = FontVariant;
export type Member = InferSelectModel<typeof member>;
export type MemberInsert = InferInsertModel<typeof member>;
export type MemberRecord = Member;
export type Organization = InferSelectModel<typeof organization>;
export type OrganizationInsert = InferInsertModel<typeof organization>;
export type OrganizationRecord = Organization;
export type ReminderRule = InferSelectModel<typeof reminder_rule>;
export type ReminderRuleInsert = InferInsertModel<typeof reminder_rule>;
export type ReminderRuleRecord = ReminderRule;
export type ServiceRecord = InferSelectModel<typeof service_record>;
export type ServiceRecordInsert = InferInsertModel<typeof service_record>;
export type ServiceRecordRecord = ServiceRecord;
export type Session = InferSelectModel<typeof session>;
export type SessionInsert = InferInsertModel<typeof session>;
export type SessionRecord = Session;
export type Themes = InferSelectModel<typeof themes>;
export type ThemesInsert = InferInsertModel<typeof themes>;
export type ThemesRecord = Themes;
export type UsageReading = InferSelectModel<typeof usage_reading>;
export type UsageReadingInsert = InferInsertModel<typeof usage_reading>;
export type UsageReadingRecord = UsageReading;
export type User = InferSelectModel<typeof user>;
export type UserInsert = InferInsertModel<typeof user>;
export type UserRecord = User;
export type Warranty = InferSelectModel<typeof warranty>;
export type WarrantyInsert = InferInsertModel<typeof warranty>;
export type WarrantyRecord = Warranty;
